<template>
  <g ref="wrapper" style="pointer-events: none">
    <template v-if="state.started">
      <path ref="pathRef" :d="pathString" class="tools path-tool path" />
      <g>
        <line
          v-for="(line, index) of indicatorLines"
          :key="index"
          :x1="line[0]"
          :y1="line[1]"
          :x2="line[2]"
          :y2="line[3]"
          class="tools path-tool indicator-line"
          vector-effect="non-scaling-stroke"
        />
        <template v-for="([leftX, leftY, posX, posY, rightX, rightY], index) in currentPathArray" :key="index">
          <template v-if="!isSame(posX, posY, rightX, rightY)">
            <line
              :x1="posX"
              :y1="posY"
              :x2="rightX"
              :y2="rightY"
              class="tools path-tool handle-line"
              vector-effect="non-scaling-stroke"
            />
            <circle
              v-if="index !== currentPathArray.length - 1"
              :cx="rightX"
              :cy="rightY"
              :r="15 * factor"
              class="tools path-tool handle"
              vector-effect="non-scaling-stroke"
            />
          </template>

          <template v-if="!isSame(posX, posY, leftX, leftY)">
            <line
              :x1="posX"
              :y1="posY"
              :x2="leftX"
              :y2="leftY"
              class="tools path-tool handle-line"
              vector-effect="non-scaling-stroke"
            />
            <circle
              :cx="leftX"
              :cy="leftY"
              :r="15 * factor"
              class="tools path-tool handle"
              vector-effect="non-scaling-stroke"
            />
          </template>

          <rect
            :ref="(ref) => ((lastNodeRef as any) = !state.done ? ref : lastNodeRef)"
            :width="30 * factor"
            :height="30 * factor"
            :x="posX - 15 * factor"
            :y="posY - 15 * factor"
            class="tools path-tool node"
            :class="{ 'node-touch-area': !state.done }"
            vector-effect="non-scaling-stroke"
          />

          <rect
            v-if="index === 0"
            :ref="(ref) => ((currentFirstNode as any) = ref)"
            :width="60 * factor"
            :height="60 * factor"
            :x="posX - 30 * factor"
            :y="posY - 30 * factor"
            class="tools path-tool node"
            :class="{ 'node-touch-area': state.done }"
            vector-effect="non-scaling-stroke"
          />
        </template>
      </g>
    </template>
  </g>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { getPointFromEvent, isSame, reflect } from './path-tool-utils';
import { usePathTool } from './usePathTool';
import Application from '../application';
import eventbus from 'tiny-emitter/instance';

/* TODO
  - Add support for smoothing
  - Make nodes editable. Hovering a node should hide the node following the cursor.
    - Finishing would happen with doubleclick on the first node
  - Revoke drawing promise if nonesense path was drawn
*/

export type ToolState = {
  // The position of the node
  pos: [x: number, y: number];
  // Did we start drawing the path?
  started: boolean;
  // Did we move while drawing one node (means we want control points)
  moved: boolean;
  // Are we done drawing our (at least first) node? (means we want a line to the cursor at all times for visualisation)
  done: boolean;
  // Did a pointerdown on the first node occurs (to close the path)
  finishing: boolean;
  // The path data itself that holds all nodes
  path: [leftX: number, leftY: number, x: number, y: number, rightX: number, rightY: number][];
  // The left control point / handle position
  left: [x: number, y: number];
  // The right control point / handle position
  right: [x: number, y: number];
  // the line that is of interest and is currently snapped to
  interestLine?: [[number, number], [number, number]];
};

const props = defineProps<{
  smoothing?: boolean;
  onFinish: (data: { d: string; bbox: DOMRect }) => void;
}>();

const wrapper = ref<SVGGElement>();
const pathRef = ref<SVGPathElement>();
const currentFirstNode = ref<SVGRectElement>();
const lastNodeRef = ref<SVGRectElement>();
const zoom = ref(Application.instance.zoomManager.zoom());

const updateZoom = (newZoom: number) => {
  zoom.value = Application.instance.zoomManager.zoom();
};

const factor = computed(() => 1 / (zoom.value * 4));

eventbus.on('camera:zoomed', updateZoom, this);

const state: ToolState = reactive({
  pos: [0, 0],
  started: false,
  moved: false,
  done: false,
  finishing: false,
  path: [],
  left: [0, 0],
  right: [0, 0],
});

const { svg, indicatorLines, mouse, cross, snappedMouse } = usePathTool(wrapper, state.path, factor);

const pathString = computed(() => {
  let d = state.path.reduce((acc: string, [leftX, leftY, posX, posY], index, arr) => {
    if (index === 0) {
      return 'M' + posX + ',' + posY;
    }

    if (isSame(posX, posY, leftX, leftY) && props.smoothing && index && arr[index + 1]) {
      // smooth out control points by applying tangent at the point that is halfway between the last and next point
      // and then find controlpoints on that tangent and use a quarter of the distance between the current and last / current and next point
      const [lastPosX, lastPosY] = arr[index - 1].slice(-4);
      const [nextPosX, nextPosY] = arr[index + 1].slice(2, 4);

      const direction = [nextPosX - lastPosX, nextPosY - lastPosY];
      const len = Math.sqrt(direction[0] * direction[0] + direction[1] * direction[1]);
      const lenLeft = [posX - lastPosX, posY - lastPosY];
      const lenRight = [nextPosX - posX, nextPosY - posY];

      const newLeft: [number, number] = [
        posX - ((direction[0] / len) * lenLeft[0]) / 2,
        posY - ((direction[1] / len) * lenLeft[1]) / 2,
      ];

      const newRight = [
        posX + ((direction[0] / len) * lenRight[0]) / 2,
        posY + ((direction[1] / len) * lenRight[1]) / 2,
      ];

      leftX = newLeft[0];
      leftY = newLeft[1];

      arr[index] = [leftX, leftY, posX, posY, newRight[0], newRight[1]];
    }

    // get last element
    const [lastRightX, lastRightY] = arr[index - 1].slice(-2);
    return `${acc} C${lastRightX},${lastRightY} ${leftX},${leftY} ${posX},${posY}`;
  }, '');

  if (state.path.length) {
    if (state.finishing && state.done) {
      const first = state.path[0];
      if (
        isSame(state.left[0], state.left[1], first[0], first[1]) &&
        isSame(state.right[0], state.right[1], first[4], first[5]) &&
        isSame(state.pos[0], state.pos[1], first[2], first[3])
      ) {
        d += ' Z';
      }
    } else {
      const [lastRightX, lastRightY] = state.path[state.path.length - 1].slice(-2);
      d += ` C${lastRightX},${lastRightY} ${state.left[0]},${state.left[1]} ${state.pos[0]},${state.pos[1]}`;
    }
  } else {
    d = 'M' + state.pos[0] + ',' + state.pos[1];
  }

  return d;
});

const currentPathArray = computed(() => [...state.path, [...state.left, ...state.pos, ...state.right]]);

const finish = (event: PointerEvent) => {
  props.onFinish({
    d: pathString.value,
    bbox: pathRef.value!.getBBox(),
  });
};

const onPointerDown = (event: PointerEvent) => {
  // on right click, delete last node
  if (event.button === 2) {
    event.preventDefault();
    deleteNode();
    return;
  }

  svg.value.addEventListener('pointerup', onPointerUp);

  const snapped = snappedMouse.value;

  state.pos = snapped;
  state.left = snapped;
  state.right = snapped;
  state.moved = false;
  state.done = false;
  state.started = true;
  state.interestLine = undefined;
  state.finishing = event.target === currentFirstNode.value;

  // The path should be closed, that means we dont want mouse coordinates but instead the coordinates of the first node
  if (state.finishing) {
    const [leftX, leftY, posX, posY, rightX, rightY] = state.path[0];
    state.left = [leftX, leftY];
    state.pos = [posX, posY];
    state.right = [rightX, rightY];
  }
};

const onPointerMove = (event: PointerEvent) => {
  if (!state.started) return;

  const { clientX, clientY } = getPointFromEvent(event, { node: wrapper.value! });

  const target = event.target;

  // this is only for snapping on mouse move when the user did not click yet
  // snap to the first point of the path to close it
  if (target === currentFirstNode.value && state.done) {
    const [leftX, leftY, posX, posY, rightX, rightY] = state.path[0];
    state.left = [leftX, leftY];
    state.pos = [posX, posY];
    state.right = [rightX, rightY];
    return;
  }

  // when we hover the last node with our handles, we want the handles to snap to it
  // as indicator that you can drop the handles there
  if (target === lastNodeRef.value && !state.done) {
    if (!event.shiftKey) {
      state.right = state.pos;
    }
    state.left = state.pos;
    return;
  }

  state.right = [clientX, clientY];

  // When the user clicked on the first node, we are overriding the handles of the first node as well
  // to be able to control the connection between last and first node
  if (state.finishing) {
    state.path[0][4] = clientX;
    state.path[0][5] = clientY;
  }

  if (state.done) {
    const lastPoint = state.path[state.path.length - 1]?.slice(2, 4) as [number, number];
    state.pos = state.right = snappedMouse.value;
  }

  // when we are not setting control points yet (state.done), we dont want that shift does anything
  if (!event.shiftKey || state.done) {
    state.left = reflect(state.right, state.pos);
    // same as above but for the left handle
    if (state.finishing) {
      state.path[0][0] = state.left[0];
      state.path[0][1] = state.left[1];
    }
  }

  if (!state.done) {
    state.moved = true;
  }
};

const onPointerUp = (event: PointerEvent) => {
  svg.value.removeEventListener('pointerup', onPointerUp);

  state.path.push([...state.left, ...state.pos, ...state.right]);
  state.done = true;

  // This is the actual last event that occurs before our path is finished
  // So we reset everything to the initial state and resolve the promise
  if (state.finishing) {
    finish(event);

    state.done = false;
    state.moved = false;
    state.started = false;
    state.pos = [0, 0];
    state.left = [0, 0];
    state.right = [0, 0];
    state.path.length = 0;
    state.interestLine = undefined;
    window.removeEventListener('keydown', onKeyDown, { capture: true });
    svg.value.removeEventListener('pointerdown', onPointerDown);
    svg.value.removeEventListener('pointermove', onPointerMove);
  } else {
    onPointerMove(event);
  }
};

const deleteNode = () => {
  if (state.path.length === 1) {
    return onCancel();
  }
  state.path.pop();
};

const onCancel = () => {
  state.done = false;
  state.moved = false;
  state.started = false;
  state.pos = [0, 0];
  state.left = [0, 0];
  state.right = [0, 0];
  state.path.length = 0;
};

const onKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    if (!state.path.length) return;
    onCancel();
    event.stopImmediatePropagation();
  }
};

const preventDefault = (event: Event) => {
  event.preventDefault();
};

onMounted(() => {
  svg.value.addEventListener('pointerdown', onPointerDown);
  svg.value.addEventListener('pointermove', onPointerMove);
  svg.value.addEventListener('contextmenu', preventDefault);
  window.addEventListener('keydown', onKeyDown, { capture: true });
});

onBeforeUnmount(() => {
  eventbus.off('camera:zoomed', updateZoom);
  svg.value.removeEventListener('pointerdown', onPointerDown);
  svg.value.removeEventListener('pointermove', onPointerMove);
  svg.value.removeEventListener('contextmenu', preventDefault);
  window.removeEventListener('keydown', onKeyDown, { capture: true });
});
</script>
